import type { OrderComment } from '@/infrastructure/FulfillmentAdminClient';

/**
 * A thread holds two kinds of note (SSD-7265): one about the order, and one about a single product
 * line, which carries that line's number. The pane shows the first kind; each line and its matching scan banner show its own.
 */
export const isLineMemo = (comment: OrderComment): comment is OrderComment & { lineNo: number } =>
  typeof comment.lineNo === 'number';

/** The notes about the order as a whole, in the order the server listed them. */
export const orderComments = (comments: readonly OrderComment[]): OrderComment[] =>
  comments.filter((comment) => !isLineMemo(comment));

/** The notes about one line, in the order they were written, so the newest sits last. */
export const lineMemos = (comments: readonly OrderComment[], lineNo: number): OrderComment[] =>
  comments.filter((comment) => comment.lineNo === lineNo);

/**
 * What the pane lists: the notes about the order, plus any memo whose line is not among the rows on screen.
 * A line removed or merged away keeps its memo, and with no row left to sit under, the pane is the only place
 * it can still be read and counted.
 */
export const paneComments = (comments: readonly OrderComment[], shownLineNos: Iterable<number>): OrderComment[] => {
  const shown = new Set(shownLineNos);
  return comments.filter((comment) => !isLineMemo(comment) || !shown.has(comment.lineNo));
};
